import { ACCESS_MODES, persistAccessMode } from "./accessMode";

export const RESTAURANT_INVITE_SESSION_KEY = "venaqqi_restaurant_invite_session_v1";
export const RESTAURANT_INVITE_DURATION_MS = 30 * 60 * 1000;

// Temporary guest-access invitations for restaurant outreach.
// Each invite grants 30 minutes of GUEST access and keeps restaurant/channel attribution.
export const RESTAURANT_INVITES = Object.freeze({
  "TEST-ALAN-20260915": Object.freeze({
    restaurantId: "base-1",
    restaurantName: "FRULLATTI Angelópolis",
    placeSlug: "frullatti-angelopolis",
    test: true,
  }),
  "VQ-R01-FRULLATTI-260926": Object.freeze({
    restaurantId: "base-1",
    restaurantName: "FRULLATTI Angelópolis",
    placeSlug: "frullatti-angelopolis",
    test: false,
  }),
  "VQ-R02-TACOQUETO-260926": Object.freeze({
    restaurantId: "base-2",
    restaurantName: "TACOQUETO – Galerías Serdán",
    placeSlug: "tacoqueto-galerias-serdan",
    test: false,
  }),
  "VQ-R03-LALO-260926": Object.freeze({
    restaurantId: "base-3",
    restaurantName: "Tacos LALO – Vistahermosa",
    placeSlug: "tacos-lalo-vistahermosa",
    test: false,
  }),
  "VQ-R04-SAPORO-260926": Object.freeze({
    restaurantId: "base-4",
    restaurantName: "Saporo & Tokio RAMEN",
    placeSlug: "saporo-tokio-ramen",
    test: false,
  }),
  "VQ-R05-MCCARTHYS-260926": Object.freeze({
    restaurantId: "base-5",
    restaurantName: "McCarthy's Irish Pub",
    placeSlug: "mccarthy-s-irish-pub",
    test: false,
  }),
  "VQ-R06-BICI-260926": Object.freeze({
    restaurantId: "base-6",
    restaurantName: "La Bici de Cleta",
    placeSlug: "la-bici-de-cleta",
    test: false,
  }),
  "VQ-R07-270926": Object.freeze({
    restaurantId: "base-7",
    restaurantName: "La Churrasca Buffet",
    placeSlug: "la-churrasca-buffet",
    test: false,
  }),
  "VQ-R08-270926": Object.freeze({
    restaurantId: "base-8",
    restaurantName: "La Pizca Angelópolis",
    placeSlug: "la-pizca-angelopolis",
    test: false,
  }),
  "VQ-R09-280926": Object.freeze({
    restaurantId: "base-9",
    restaurantName: "Rinconcito Argentino",
    placeSlug: "rinconcito-argentino",
    test: false,
  }),
  "VQ-R10-280926": Object.freeze({
    restaurantId: "base-10",
    restaurantName: "La Chata Burgers Gourmet",
    placeSlug: "la-chata-burgers-gourmet",
    test: false,
  }),
  "VQ-R11-280926": Object.freeze({
    restaurantId: "base-11",
    restaurantName: "Las Delicias de Mamá Eva",
    placeSlug: "las-delicias-de-mama-eva",
    test: false,
  }),
  "VQ-R12-290926": Object.freeze({
    restaurantId: "base-12",
    restaurantName: "El Asador Restaurante",
    placeSlug: "el-asador-restaurante",
    test: false,
  }),
});

const localStore = () => typeof window !== "undefined" ? window.localStorage : undefined;
const browserLocation = () => typeof window !== "undefined" ? window.location : undefined;
const browserHistory = () => typeof window !== "undefined" ? window.history : undefined;

const safeParse = value => {
  try { return JSON.parse(value || "null"); } catch { return null; }
};

export const readRestaurantInviteSession = (storage = localStore(), now = Date.now()) => {
  if (!storage) return null;
  const parsed = safeParse(storage.getItem(RESTAURANT_INVITE_SESSION_KEY));
  if (!parsed?.inviteId || !RESTAURANT_INVITES[parsed.inviteId]) return null;
  if (!Number(parsed.expiresAt) || now >= Number(parsed.expiresAt)) return null;
  return parsed;
};

export const clearRestaurantInviteSession = (storage = localStore()) => {
  if (!storage) return;
  try { storage.removeItem(RESTAURANT_INVITE_SESSION_KEY); } catch { /* no-op */ }
  persistAccessMode(ACCESS_MODES.PENDING, storage);
};

export const expireStaleRestaurantInviteSession = (storage = localStore(), now = Date.now()) => {
  if (!storage) return false;
  const parsed = safeParse(storage.getItem(RESTAURANT_INVITE_SESSION_KEY));
  if (!parsed) return false;
  if (readRestaurantInviteSession(storage, now)) return false;
  clearRestaurantInviteSession(storage);
  return true;
};

export const bootstrapRestaurantInvite = ({
  storage = localStore(),
  location = browserLocation(),
  history = browserHistory(),
  now = Date.now(),
} = {}) => {
  if (!storage || !location) return null;

  expireStaleRestaurantInviteSession(storage, now);

  const params = new URLSearchParams(location.search || "");
  const inviteId = String(params.get("invite") || "").trim();
  const source = String(params.get("source") || "direct").trim().slice(0, 40) || "direct";
  const invite = RESTAURANT_INVITES[inviteId];

  if (!invite) {
    const active = readRestaurantInviteSession(storage, now);
    if (active) persistAccessMode(ACCESS_MODES.GUEST, storage);
    return active ? { ...active, openedNow: false } : null;
  }

  const existing = readRestaurantInviteSession(storage, now);
  const sameActiveInvite = existing?.inviteId === inviteId;
  const session = sameActiveInvite ? existing : {
    version: 1,
    inviteId,
    restaurantId: invite.restaurantId,
    restaurantName: invite.restaurantName,
    placeSlug: invite.placeSlug,
    source,
    test: Boolean(invite.test),
    startedAt: now,
    expiresAt: now + RESTAURANT_INVITE_DURATION_MS,
  };

  if (!sameActiveInvite) {
    try { storage.setItem(RESTAURANT_INVITE_SESSION_KEY, JSON.stringify(session)); } catch { /* no-op */ }
  }
  persistAccessMode(ACCESS_MODES.GUEST, storage);

  // The existing public place deep-link mechanism in App resolves ?lugar=<slug>.
  // Inject the canonical invite destination before React mounts.
  if (!params.get("lugar")) {
    params.set("lugar", invite.placeSlug);
    history?.replaceState?.(history.state, "", `${location.pathname || "/"}?${params.toString()}${location.hash || ""}`);
  }

  return { ...session, openedNow: !sameActiveInvite };
};

export const restaurantInviteEventMetadata = session => session ? ({
  flow: "restaurant_invite",
  invite_id: session.inviteId,
  restaurant_id: session.restaurantId,
  restaurant_name: session.restaurantName,
  place_slug: session.placeSlug,
  invite_source: session.source,
  test: Boolean(session.test),
  expires_at: new Date(session.expiresAt).toISOString(),
}) : {};
